/**
 * Wire schemas for the "library entity" routes: Skill and Persona
 * (src/api/v4/lib/library-entity-router.ts and its three mounts,
 * user-skills.ts, user-personas.ts, admin-skills.ts).
 *
 * `*RowSchema` is a whole Prisma row, which the admin mount sends as-is.
 * `*EntrySchema` is the row the user mounts send after `format`: the row plus
 * `author`, `isFavorited`, `forkCount` and `isPinned`. No key is stripped from
 * the wire. Timestamps accept a Date (Prisma rows) or an ISO string, and a
 * Date serializes to the same ISO string as before. `visibility` is a free-text
 * column (`personal | team`, no DB constraint), so it stays `z.string()`: an
 * enum would turn one legacy value into a 500 on the whole library list.
 */
import { z } from 'zod';
import { DateOrIsoStringSchema } from './date-or-iso';

/** `personal | team`. The DB column is free text, so the row schemas keep `z.string()`; this is what writers accept and the admin page narrows to. */
export const LibraryVisibilitySchema = z.enum(['personal', 'team']);

const LibraryEntityFields = {
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  content: z.string(),
  createdById: z.number(),
  visibility: z.string(),
  forkedFromId: z.string().nullable(),
  usageCount: z.number(),
  availableIn: z.array(z.string()),
  enabled: z.boolean(),
  createdAt: DateOrIsoStringSchema,
  updatedAt: DateOrIsoStringSchema,
};

export const SkillRowSchema = z.object({
  ...LibraryEntityFields,
  slug: z.string(),
  requiresTools: z.array(z.string()),
});

const PersonaRowSchema = z.object(LibraryEntityFields);

const LibraryEntryExtras = {
  author: z.object({
    id: z.number(),
    slackUserId: z.string(),
    ownerEmail: z.string().nullable(),
  }),
  isFavorited: z.boolean(),
  forkCount: z.number(),
  isPinned: z.boolean(),
};

export const SkillEntrySchema = SkillRowSchema.extend(LibraryEntryExtras);
export const PersonaEntrySchema = PersonaRowSchema.extend(LibraryEntryExtras);

/** POST /:id/favorite: the new state of the toggle. */
export const FavoriteToggleResponseSchema = z.object({ favorited: z.boolean() });
